import React, {useState} from 'react';
import {Card} from "../../../../../../components/card/card.jsx";
import {FaCar, FaExternalLinkAlt, FaHistory, FaUser} from "react-icons/fa";
import {getAssetPath} from "../../../../../../utils/get-asset-path.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import Button from "../../../../../../components/button/button.jsx";
import {navigateHook} from "../../../../../../hooks/use-navigate.js";
import ProtectionTypes from "../../../citizen/utils/protection-type.js";
import ViolenceType from "../../../citizen/utils/violence-type.js";
import prepareAddress from "../utils/prepareAddress.js";
import {FaHouse, FaRoute} from "react-icons/fa6";
import {Modal} from "../../../../../../components/modal/modal.jsx";
import SentryRow from "../../../../../../components/template/sentry-row.jsx";
import PlatePatternUtils from "../../../../../../utils/plate-pattern-utils.js";
import useHasPermission from "../../../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../../../utils/enums/permission-enum.js";
import clsx from "clsx";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const isUrl = (text) => {
    try { new URL(text); return true; } catch { return false; }
};

const SosCitizenInfo = ({event, location, citizen, apiData, target, isApi, defaultHeight, actionRef, hasRoute, showRoute, setShowRoute}) => {
    const navigate = navigateHook();
    const hasPermission = useHasPermission();

    const [open, setOpen] = useState(false);
    const [modalTarget, setModalTarget] = useState(undefined);
    const [modalTitle, setModalTitle] = useState("");

    const openModal = (target, modalTitle) => {
        setModalTarget(target);
        setModalTitle(modalTitle);
        setOpen(true);
    }

    const closeModal = () => {
        setOpen(false);
        setModalTarget(undefined);
        setModalTitle("");
    }

    const isAddressSelected = (address) => {
        if (!target?.includes("address")) return false;
        const id = target.split("address-")[1];
        return id === address.id || id === address?.placeId;
    };

    const formPermission = hasPermission(PermissionEnum.SOS_MANAGE_CITIZEN) ||  hasPermission(PermissionEnum.SOS_MANAGE_EVENTS) || hasPermission(PermissionEnum.SOS_VIEW);

    const citizenCard = (
        <section key={"citizen"} id={"citizen"} className={"space-y-3"}>
            <h3 className={"flex gap-2 items-center"}>
                    <span className={["text-white w-7.5 h-7.5 rounded-[50%] inline-flex justify-center items-center", target === "citizen" ? "bg-[#ff2121]" : "bg-(--blue-dark)"].join(" ")}>
                        <FaUser />
                    </span>
                {isApi ? apiData?.identifier : "Protegido"}
            </h3>

            <Card className={"cursor-pointer " + (target === "citizen" ? "border-[#ff2121]! bg-[#ffeeee]!" : "")} onClick={() => actionRef?.current.click("citizen")}>
                <Card.Body className={"grid grid-cols-12 gap-2"}>
                    <div className={"col-span-4"}>
                        <div className={"h-[120px] w-full m-1 items-center report-center"}>
                            <img
                                className={"max-h-full"}
                                src={citizen?.victimPicture ? `${API_KEEPER}/ged/attach/${citizen.victimPicture}` : getAssetPath("img/user.png")}
                                alt=""
                                onError={(e) => e.target.src = getAssetPath("img/user.png")}
                            />
                        </div>
                    </div>
                    <div className={"col-span-8 space-y-2"}>
                        <h3>{citizen?.name}</h3>
                        <p>{projectUtils.formatPhone(citizen?.phone)}</p>
                        <p className={"text-[#f17600] animate-pulse"}>
                            {
                                (location?.dtLocation || location?.dtLocal)
                                    ? "Última localização " + (location?.dtLocation || location?.dtLocal)
                                    : <strong>Não foi possível obter a localização</strong>
                            }
                        </p>
                        <div className={"flex flex-wrap gap-1"}>
                            {
                                !isApi && formPermission &&
                                <Button title={"Informações do protegido"} onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/sos/citizens/form/${citizen.id}`, true)
                                }}>
                                    <FaExternalLinkAlt />
                                </Button>
                            }

                            {
                                !isApi && hasPermission(PermissionEnum.SOS_CITIZEN_HISTORY_EDIT) &&
                                <Button title={"Histórico do protegido"} onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/sos/citizen/history/${citizen.id}`, true)
                                }}>
                                    <FaHistory />
                                </Button>
                            }

                            {
                                !!citizen?.vehicles?.length &&
                                <Button title={"Veículos do protegido"} onClick={(e) => {
                                    e.stopPropagation();
                                    openModal(citizen.vehicles, "Protegido")
                                }}>
                                    <FaCar />
                                </Button>
                            }

                            {
                                hasRoute &&
                                <Button
                                    target={showRoute ? "warning" : "primary"}
                                    title={showRoute
                                        ? "Ocultar trajeto"
                                        : "Exibir trajeto"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowRoute(prev => !prev);
                                    }}
                                >
                                    <img src={getAssetPath("img/sos/map-markers/route-icon.svg")} alt={""} />
                                </Button>
                            }

                            {event?.gpsOff && <span className={"text-red font-bold"}>GPS desligado</span>}
                        </div>

                    </div>
                </Card.Body>
            </Card>
        </section>
    );

    const aggressorsCard = citizen?.aggressors?.length ? (
        <section key={"aggressors"} id={"aggressors"} className={"space-y-3"}>
            <h3 className={"flex gap-2 items-center"}>
                <span className={"text-white w-7.5 h-7.5 rounded-[50%] inline-flex justify-center items-center bg-(--blue-dark)"}>
                    <FaUser />
                </span>
                Agressor{citizen.aggressors.length > 1 ? "es" : ""}
            </h3>

            {
                citizen.aggressors.map((a, i) => (
                    <Card className={"cursor-pointer"} key={"aggressor-" + a.itemId} onClick={() => actionRef?.current?.click("aggressor-" + a.itemId)}>
                        <Card.Body className={"grid grid-cols-12 gap-2"}>
                            <div className={"col-span-4"}>
                                <div className={"h-[120px] w-full m-1 items-center report-center"}>
                                    <img
                                        className={"max-h-full"}
                                        src={a.attachId ? `${API_KEEPER}/ged/attach/${a.attachId}` : getAssetPath("img/user.png")}
                                        alt=""
                                        onError={(e) => e.target.src = getAssetPath("img/user.png")}
                                    />
                                </div>
                            </div>
                            <div className={"col-span-8 space-y-2"}>
                                <h3>
                                    {a.name}
                                    {
                                        a.firearmUseHistory &&
                                        <div className={"p-1 !text-[12px] rounded-sm text-[#ed5565] bg-[#f9e1e5]"}>
                                            Possui histórico de uso de arma de fogo
                                        </div>
                                    }
                                </h3>
                                {a.protectionType && <p>Tipo de medida: <br/><strong>{ProtectionTypes.toView(a.protectionType)}</strong></p>}
                                {a.violenceTypes?.length > 0 && <p>Tipo de violência: <br/><strong>{ViolenceType.toView(a.violenceTypes)}</strong></p>}
                                {a.description && <p>Descrição: <br/><strong>{a.description}</strong></p>}
                                {
                                    !!a?.vehicles?.length &&
                                    <Button title={"Veículos do agressor"} onClick={() => openModal(a.vehicles, `Agressor ${i+1}`)}>
                                        <FaCar />
                                    </Button>
                                }
                            </div>
                        </Card.Body>
                    </Card>
                ))
            }
        </section>
    ) : null;

    const addressesCard = citizen?.addresses?.length ? (
        <section key={"protectedPlaces"} id={"protectedPlaces"} className={"space-y-3"}>
            <h3 className={"flex gap-2 items-center"}>
                <span className={clsx("text-white w-[30px] h-[30px] rounded-[50%] inline-flex justify-center items-center", target?.includes("address") ? "bg-[#ff2121]" : "bg-(--blue-dark)")}>
                    <FaHouse />
                </span>
                {citizen.addresses.length > 1 ? "Endereços do protegido" : "Endereço do protegido"}
            </h3>

            {
                citizen.addresses.map((p) => (
                    <Card className={[isAddressSelected(p) ? "!border-[#ff2121] !bg-[#ffeeee]" : "", p.latitude && p.longitude ? "cursor-pointer" : ""].join(" ")} key={p.id} onClick={() => actionRef?.current?.click("address-" + p.id)}>
                        <Card.Body className={"grid grid-cols-12 gap-2"}>
                            <div className={"col-span-4"}>
                                <div className={"h-[120px] w-full m-1 items-center report-center"}>
                                    <img
                                        className={"max-h-full"}
                                        src={p.attachId ? `${API_KEEPER}/ged/attach/${p.attachId}` : getAssetPath("img/no-img.png")}
                                        alt=""
                                        onError={(e) => e.target.src = getAssetPath("img/no-img.png")}
                                    />
                                </div>
                            </div>
                            <div className={"col-span-8 space-y-2"}>
                                <h3>{p.description}</h3>
                                <p>{prepareAddress(p)}</p>
                            </div>
                        </Card.Body>
                    </Card>
                ))
            }
        </section>
    ) : null;

    const extraCard = citizen?.extraDescription ? (
        <section className={"space-y-3"} key={"extra-info"}>
            <h3 className={"flex gap-2 items-center"}>Descrição extra</h3>
            <Card>
                <Card.Body>
                    {
                        isUrl(citizen.extraDescription)
                            ? <a href={citizen.extraDescription} target="_blank" rel="noopener noreferrer" className={"wrap-break-word hover:text-blue-600"}>{citizen.extraDescription}</a>
                            : citizen.extraDescription
                    }
                </Card.Body>
            </Card>
        </section>
    ) : null;

    const sortCards = () => {
        const base = [citizenCard, aggressorsCard, extraCard].filter(Boolean);
        if (target?.includes("address-") && addressesCard) return [addressesCard, ...base];
        return addressesCard ? [...base, addressesCard] : base;
    }

    return (
        <>
            <Card className={["overflow-auto", defaultHeight].join(" ")}>
                <Card.Body className={"space-y-3"}>
                    {sortCards()}
                    {
                        apiData?.vehicle &&
                        <section id={"protectedPlaces"} className={"space-y-3"}>
                            <h3>Veículo</h3>
                            <Card>
                                <Card.Body className={"space-y-2"}>
                                    <h4>{apiData?.vehicle.plate}</h4>
                                    <p>Marca/Modelo: <strong>{apiData?.vehicle.brandModel}</strong></p>
                                    <p>Cor: <strong>{apiData?.vehicle.color}</strong></p>
                                    <p>Ano de fabricação: <strong>{apiData?.vehicle.fabricationYear}</strong></p>
                                    <p>Ano do modelo: <strong>{apiData?.vehicle.modelYear}</strong></p>
                                </Card.Body>
                            </Card>
                        </section>
                    }
                </Card.Body>
            </Card>

            <Modal open={open} setOpen={closeModal} title={`Veículos do ${modalTitle}`} className={"w-6/12"}>
                <Modal.Body className={"space-y-4"}>
                    {
                        modalTarget?.map(vehicle => {
                            const plate = PlatePatternUtils.formatted(vehicle.plate);

                            return (
                                <Card>
                                    <Card.Body>
                                        <SentryRow>
                                            <div className="col-span-4">
                                                <div className="vehicle-plate"
                                                     style={{width: "75%", justifySelf: "center"}}>
                                                    <div
                                                        className={"product-heading " + (plate.pattern ?? 'WITHOUT_PATTERN')}></div>
                                                    <div
                                                        className={"product-body " + (plate.pattern ?? 'WITHOUT_PATTERN')}>
                                                        {plate.formattedValue}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-span-8">
                                                Descrição: <strong>{vehicle.description}</strong>
                                            </div>
                                        </SentryRow>
                                    </Card.Body>
                                </Card>
                            )
                        })
                    }
                </Modal.Body>
            </Modal>
        </>
    );
};

export default SosCitizenInfo;